import { useEffect, useRef, useState } from "react";
import { useAnnotationEvents } from "../lib/annotations/AnnotationEvents.context";
import { hexToRgb } from "@/lib/utils";
import { useNutrientAnnotationEvents } from "@/lib/annotations/useNutrientAnnotationEvents";

interface NutrientPDFViewerProps {
	documentUrl: string;
	onReady?: (instance: NutrientViewerInstance) => void;
	onError?: (error: string) => void;
	className?: string;
	onAnnotationsChange?: (instantJson: InstantJSON) => void;
	initialAnnotations?: InstantJSON;
	/** Hex color string like #RRGGBB used for ink stroke */
	inkColorHex: string;
	/** When true, disables annotation tools and events */
	readOnly?: boolean;
}

/**
 *
 * @param documentUrl - The URL of the PDF document to load
 * @param onReady - Callback function when the viewer is ready
 * @param onError - Callback function when an error occurs
 * @param className - Optional className for the container
 * @param onAnnotationsChange - Callback function when the annotations change
 * @param initialAnnotations - Optional initial annotations
 * @returns
 */
export default function NutrientPDFViewer({
	documentUrl,
	onReady,
	onError,
	className = "",
	onAnnotationsChange,
	initialAnnotations,
	inkColorHex,
	readOnly = false,
}: NutrientPDFViewerProps) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const instanceRef = useRef<NutrientViewerInstance | null>(null);
	const [viewerInstance, setViewerInstance] =
		useState<NutrientViewerInstance | null>(null);
	const [isInitializing, setIsInitializing] = useState(true);
	const { onAnnotationsChange: ctxOnAnnotationsChange, onError: ctxOnError } =
		useAnnotationEvents();

	// Register annotation events via hook only when not read-only
	useNutrientAnnotationEvents(
		readOnly ? null : viewerInstance,
		readOnly
			? { initialAnnotations }
			: {
					initialAnnotations,
					onAnnotationsChange,
					ctxOnAnnotationsChange,
					onError,
					ctxOnError,
				},
	);

	useEffect(() => {
		let cancelled = false;
		let timeoutId: NodeJS.Timeout;

		const waitForContainer = async (): Promise<HTMLElement> => {
			return new Promise((resolve, reject) => {
				const checkContainer = () => {
					if (cancelled) {
						reject(new Error("Component unmounted"));
						return;
					}

					const container = containerRef.current;
					if (container) {
						resolve(container);
					} else {
						timeoutId = setTimeout(checkContainer, 50);
					}
				};
				checkContainer();
			});
		};

		const initializeNutrient = async () => {
			try {
				setIsInitializing(true);

				// Wait for container to be available
				const container = await waitForContainer();

				if (cancelled) return;

				// Check if NutrientViewer is available
				const nutrientViewer = window.NutrientViewer;
				if (!nutrientViewer) {
					throw new Error("NutrientViewer is not available on window");
				}

				// Defensive: make sure no previous instance is still mounted on this container.
				// This can happen if the component re-renders/re-initializes quickly while a
				// previous load is still in-flight.
				try {
					nutrientViewer.unload(container);
				} catch {
					// ignore
				}

				// Configure annotation presets
				const annotationPresets = nutrientViewer.defaultAnnotationPresets;
				const rgb = hexToRgb(inkColorHex);
				annotationPresets.ink = {
					...annotationPresets.ink,
					lineWidth: 1,
					strokeColor: new nutrientViewer.Color(rgb),
					opacity: 0.7,
				};

				const styleSheetUrl = new URL(
					"/nutrient-custom.css",
					window.location.origin,
				).toString();

				const loadOptions = {
					container,
					document: documentUrl,
					enableHistory: !readOnly,
					styleSheets: [styleSheetUrl],
					// disable inline text selection toolbar
					inlineTextSelectionToolbarItems: () => {
						return [];
					},
					instantJSON: initialAnnotations ?? undefined,
					toolbarItems: readOnly
						? [{ type: "pager" }, { type: "zoom-in" }, { type: "zoom-out" }]
						: [
								{ type: "pan" },
								{ type: "ink" },
								{ type: "ink-eraser", dropdownGroup: "my-group" },
								{ type: "pager" },
								{ type: "zoom-in" },
								{ type: "zoom-out" },
								{ type: "undo" },
								{ type: "redo" },
							],
					initialViewState: new nutrientViewer.ViewState({
						canScrollWhileDrawing: true,
						enableAnnotationToolbar: false,
					}),
					annotationPresets,
				} as NutrientViewerLoadOptions & { styleSheets: string[] };

				// Load Nutrient instance
				const instance = await nutrientViewer.load(loadOptions);

				if (cancelled) {
					try {
						nutrientViewer.unload(container);
					} catch {
						// ignore
					}
					return;
				}

				instanceRef.current = instance;
				setViewerInstance(instance);
				setIsInitializing(false);
				onReady?.(instance);
			} catch (err) {
				if (!cancelled) {
					console.error("Error initializing Nutrient PDF viewer:", err);
					const errorMessage =
						err instanceof Error
							? err.message
							: "Error initializing PDF viewer";
					setIsInitializing(false);
					onError?.(errorMessage);
				}
			}
		};

		initializeNutrient();

		return () => {
			cancelled = true;
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			// Cleanup Nutrient instance (always try to unload the container).
			// IMPORTANT: do NOT require instanceRef.current to be set, because load()
			// might have mounted already even if our instance ref was never assigned.
			if (containerRef.current) {
				try {
					const nutrientViewer = window.NutrientViewer;
					if (nutrientViewer) {
						nutrientViewer.unload(containerRef.current);
					}
				} catch {
					// Ignore cleanup errors
				} finally {
					instanceRef.current = null;
				}
			}
		};
	}, [
		documentUrl,
		initialAnnotations,
		onReady,
		onError,
		inkColorHex,
		readOnly,
	]);

	return (
		<div className={`w-full h-full ${className}`}>
			<div ref={containerRef} className="w-full h-full" />
			{isInitializing && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
					<div className="flex flex-col items-center gap-4">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
						<p className="text-sm text-gray-600">
							PDF Viewer initialisieren...
						</p>
					</div>
				</div>
			)}
		</div>
	);
}
